import { useCallback, useMemo } from 'react';

import { timezoneOptions } from '@/constants/constants';
import { useDashboard } from '@/features/Dashboard/api/queries';
import { DASHBOARD_PREFIX } from '@/features/Dashboard/constants/const';
import { DASHBOARD_DEFAULT_BASE_NAME } from '@/features/Dashboard/constants/names';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';
import useSnackbarNotifications from '@/hooks/useSnackbarNotifications';
import type { Option } from '@/slices/userConfigSlice';
import { saveUserConfig } from '@/slices/userConfigSlice';

const usePreferences = () => {
  const dispatch = useAppDispatch();
  const userConfigState = useAppSelector(state => state.userConfig);
  const {
    userConfig: { timezone, dateFormat, defaultMarket: userDefaultMarket, homepage: userHomepage }
  } = userConfigState;
  const { groupedMarkets, marketsList } = useAppSelector(state => state.marketsList);
  const { data: dashboards } = useDashboard();
  const { showLoadingSnackbar, showErrorSnackbar, showSuccessSnackbar } =
    useSnackbarNotifications();

  const marketsOptions = useMemo(
    () =>
      groupedMarkets.map(group => {
        return {
          label: group.name,
          options: group.markets.map(market => ({
            label: market.name,
            value: market.marketId as string
          }))
        };
      }),
    [groupedMarkets]
  );

  const dashboardOptions = useMemo<Option[]>(() => {
    if (!dashboards) {
      return [];
    }

    return Object.keys(dashboards).map(name => ({
      label: name,
      value: DASHBOARD_PREFIX + name
    }));
  }, [dashboards]);

  const getFallbackMarketOption = useCallback(
    (userOption: Option): Option => {
      if (userOption.value.length) {
        return userOption;
      }
      const firstAvailable = marketsList[0];
      if (!firstAvailable) {
        return {
          label: '',
          value: ''
        };
      }
      return {
        label: firstAvailable.metadata.displayName,
        value: firstAvailable.marketId
      };
    },
    [marketsList]
  );

  const defaultMarket = useMemo(
    () => getFallbackMarketOption(userDefaultMarket),
    [getFallbackMarketOption, userDefaultMarket]
  );

  const homepageOptions = useMemo(
    () => [
      ...(dashboardOptions.length
        ? [
            {
              label: 'Dashboards',
              options: dashboardOptions
            }
          ]
        : [{ label: DASHBOARD_DEFAULT_BASE_NAME, value: `my-dashboard` }]),
      ...marketsOptions
    ],
    [dashboardOptions, marketsOptions]
  );

  const homepage = useMemo(() => {
    if (userHomepage.value.length) {
      return userHomepage;
    }

    if (dashboardOptions.length) {
      return dashboardOptions[0];
    }

    return { label: DASHBOARD_DEFAULT_BASE_NAME, value: 'my-dashboard' };
  }, [dashboardOptions, userHomepage]);

  const changeUserSetting = useCallback(
    (
      settingName: 'timezone' | 'dateFormat' | 'defaultMarket' | 'homepage',
      userSetting: Option,
      showSnackbar = true
    ) => {
      const config = {
        ...userConfigState.userConfig,
        [settingName]: userSetting
      };
      const key = `preferences-${settingName}`;
      if (showSnackbar) showLoadingSnackbar('Saving your changes, please wait...', key);
      dispatch(saveUserConfig({ userConfig: config }))
        .unwrap()
        .then(() => {
          if (showSnackbar) showSuccessSnackbar('Changes saved!', key);
        })
        .catch(() => {
          showErrorSnackbar(
            "Oops! We couldn't save your changes. Please try again.",
            undefined,
            key
          );
        });
    },
    [
      dispatch,
      showErrorSnackbar,
      showLoadingSnackbar,
      showSuccessSnackbar,
      userConfigState.userConfig
    ]
  );

  return {
    timezone,
    dateFormat,
    defaultMarket,
    changeUserSetting,
    timezoneOptions,
    marketsOptions,
    homepage,
    homepageOptions
  };
};

export default usePreferences;
